import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { callDelete, callGet } from '@/api/fetcher';

export interface WebSession {
  id: string;
  name: string;
  user_agent: string;
  ip: string;
  location: string;
  last_seen_at: string;
  created_at: string;
  is_current: boolean;
  is_active: boolean;
}

export interface WebSessionsResponse {
  current: string;
  web_sessions: WebSession[];
}

const QUERY_KEY = ['web-sessions'] as const;
const ENDPOINT = '/www/web-sessions';
const EMPTY: WebSession[] = [];

export const useWebSessions = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const query = useQuery<WebSessionsResponse, Error>({
    queryKey: QUERY_KEY,
    queryFn: () => callGet(ENDPOINT),
  });

  const revokeOneMutation = useMutation<void, Error, string>({
    mutationFn: (id) => callDelete(`${ENDPOINT}/${encodeURIComponent(id)}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success(t('web_sessions.revoked'));
    },
    meta: { errorToast: true },
  });

  const revokeAllMutation = useMutation<{ revoked: number }, Error, void>({
    mutationFn: () => callDelete(ENDPOINT),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success(t('web_sessions.revoked_all'));
    },
    meta: { errorToast: true },
  });

  return {
    sessions: query.data?.web_sessions ?? EMPTY,
    status: query.status,
    refetch: query.refetch,
    revokeOne: revokeOneMutation.mutateAsync,
    revokeAll: revokeAllMutation.mutateAsync,
    isRevokingOne: revokeOneMutation.isPending,
    isRevokingAll: revokeAllMutation.isPending,
  };
};
